import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Wallet, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { Money } from "./SaudiRiyal";
import { FuelChargeMode } from "./FuelChargeMode";

const thisMonth = () => new Date().toISOString().slice(0, 7);

const LimitsForm = ({ canEdit, onSaved }) => {
  const [v, setV] = useState({ monthly_limit_default: "", monthly_limit_car: "", monthly_limit_moto: "", limit_basis: "fixed", orders_allow_pct: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.fuelSettings().then((s) => setV({ monthly_limit_default: s.monthly_limit_default || "", monthly_limit_car: s.monthly_limit_car || "", monthly_limit_moto: s.monthly_limit_moto || "", limit_basis: s.limit_basis || "fixed", orders_allow_pct: s.orders_allow_pct || "" })).catch(() => {}); }, []);
  const byOrders = v.limit_basis === "orders";
  const save = async () => {
    setBusy(true);
    try {
      await api.saveFuelSettings({ monthly_limit_default: Number(v.monthly_limit_default) || 0, monthly_limit_car: Number(v.monthly_limit_car) || 0, monthly_limit_moto: Number(v.monthly_limit_moto) || 0, limit_basis: v.limit_basis, orders_allow_pct: Number(v.orders_allow_pct) || 0 });
      toast.success("تم حفظ حدود الوقود"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  const F = ({ k, label }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="number" min="0" dir="ltr" disabled={!canEdit} data-testid={`fuel-limit-${k}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="h-9" />
    </div>
  );
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2" data-testid="fuel-limit-basis">
        <span className="text-xs font-bold text-slate-600">طريقة حساب الحدّ:</span>
        {[["fixed", "مبلغ ثابت شهري"], ["orders", "حسب عدد الطلبات"]].map(([k, l]) => (
          <button key={k} type="button" disabled={!canEdit} data-testid={`fuel-limit-basis-${k}`} onClick={() => setV({ ...v, limit_basis: k })}
            className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${v.limit_basis === k ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {byOrders && (
        <div className="rounded-xl bg-sky-50 p-3 space-y-2">
          <p className="text-xs text-sky-800">الحدّ = (عدد الطلبات ÷ معيار طلب/لتر لنوع المركبة) × سعر اللتر + نسبة السماح. المندوب بدون طلبات مربوطة يُطبّق عليه الحدّ الثابت أدناه.</p>
          <div className="w-48">{F({ k: "orders_allow_pct", label: "نسبة السماح الإضافية (%)" })}</div>
        </div>
      )}
      <p className="text-xs text-slate-500">{byOrders ? "الحدود الثابتة (احتياطي لمن لا توجد له طلبات): " : ""}الأولوية: حدّ المندوب في ملفه ← حدّ نوع المركبة ← الحدّ العام. القيمة 0 تعني بدون حدّ.</p>
      <div className="grid grid-cols-3 gap-3">
        {F({ k: "monthly_limit_default", label: "الحدّ العام (⃁/شهر)" })}
        {F({ k: "monthly_limit_car", label: "حدّ السيارة" })}
        {F({ k: "monthly_limit_moto", label: "حدّ الدراجة النارية" })}
      </div>
      {canEdit && <Button size="sm" data-testid="fuel-limits-save" disabled={busy} onClick={save} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ الحدود</Button>}
    </div>
  );
};

export const FuelPayrollDeduction = ({ canEdit }) => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setData(null); api.fuelPayrollPreview(month).then(setData).catch(() => toast.error("تعذّر التحميل")); }, [month]);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    if (!window.confirm(`سيتم وضع ${data?.charge_mode === "full" ? "كامل مبلغ الوقود" : "زيادة الوقود"} في خانة «رسوم الوقود» بمسيّر رواتب ${month}. متابعة؟`)) return;
    setBusy(true);
    try {
      const r = await api.fuelPayrollApply(month);
      toast.success(`تم تطبيق الخصم على ${r.applied} راتب`);
      if (r.skipped_no_salary?.length) toast.info(`بدون مسيّر راتب لهذا الشهر: ${r.skipped_no_salary.slice(0, 5).join("، ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التطبيق"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="fuel-payroll-deduction">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><Wallet className="h-5 w-5 text-emerald-600" /> خصم زيادة الوقود من الراتب</h3>
        <div className="flex items-center gap-2">
          <Input type="month" dir="ltr" data-testid="fuel-payroll-month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" />
          {canEdit && <Button data-testid="fuel-payroll-apply" disabled={busy || !data?.riders_over} onClick={apply} className="h-9 gap-1.5 bg-rose-600 hover:bg-rose-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} تطبيق على المسيّر</Button>}
        </div>
      </div>
      <FuelChargeMode canEdit={canEdit} onChanged={load} testid="fuel-charge-mode-fuelpanel" />
      <LimitsForm canEdit={canEdit} onSaved={load} />
      {!data ? <div className="py-8 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 text-sm">
            <span className="text-slate-600">{data.charge_mode === "full" ? `${data.riders_over} مندوب — يُخصم كامل البنزين` : `${data.riders_over} مندوب تجاوز الحدّ`}</span>
            <span className="font-bold text-rose-700" data-testid="fuel-payroll-total">إجمالي الخصم: <Money value={data.total_excess} /></span>
          </div>
          {data.rows.length === 0 ? <p className="px-4 pb-6 text-center text-xs text-slate-400">لا توجد تكاليف وقود مربوطة بمناديب في هذا الشهر.</p> : (
            <div className="max-h-[28rem] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr><th className="p-2 text-right">المندوب</th><th className="p-2">الصرف</th><th className="p-2">الطلبات</th><th className="p-2">طلب/لتر</th><th className="p-2">الحدّ</th><th className="p-2">المصدر</th><th className="p-2">الخصم</th><th className="p-2">المسيّر</th></tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.employee_id} data-testid={`fuel-payroll-row-${i}`} className={`border-t border-slate-100 ${r.excess > 0 ? "bg-rose-50/40" : ""}`}>
                      <td className="p-2">{r.name}<span className="text-slate-400"> {r.branch ? `· ${r.branch}` : ""}</span>
                        {r.mode_override && <span data-testid={`fuel-payroll-override-${i}`} className="ms-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700">استثناء: {r.mode === "full" ? "كامل المبلغ" : "فوق الحد"}</span>}</td>
                      <td className="p-2 text-center font-mono-nums"><Money value={r.spent} /></td>
                      <td className="p-2 text-center font-mono-nums" data-testid={`fuel-payroll-orders-${i}`}>{r.orders ?? "—"}</td>
                      <td className="p-2 text-center font-mono-nums" data-testid={`fuel-payroll-opl-${i}`}>{r.orders_per_liter != null ? <span className={r.bench_opl && r.orders_per_liter < r.bench_opl ? "font-bold text-rose-600" : "text-emerald-700"}>{r.orders_per_liter}{r.bench_opl ? <span className="text-slate-400"> / {r.bench_opl}</span> : null}</span> : "—"}</td>
                      <td className="p-2 text-center font-mono-nums">{r.limit ? <Money value={r.limit} /> : (r.excess ? "بدون حدّ" : "—")}</td>
                      <td className="p-2 text-center text-slate-500">{r.limit_source}</td>
                      <td className="p-2 text-center font-mono-nums font-bold text-rose-700">{r.excess ? <Money value={r.excess} /> : "—"}</td>
                      <td className="p-2 text-center">{r.has_salary ? (r.current_fuel_fees ? <span className="text-emerald-700">مُطبّق <Money value={r.current_fuel_fees} /></span> : "موجود") : <span className="text-amber-600">لا يوجد</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
